/**
 * DL-02 — the download pill: ONE small overlay at the bottom CENTRE of the
 * browser edge (Christian 2026-10-22, second cut: the lower-left corner
 * covered a table's row-count text, the centre of the bottom edge carries no
 * information; a pill, not a full-width bar, so pagination at the bottom
 * right stays reachable). `position: fixed`, canvas-coloured, a hairline and
 * a faint elevation, never in flow — zero layout shift (house convention,
 * SB6-ANA-PROG-03).
 *
 * Shows while a download is running or queued, and for as long as "Pause
 * all" holds items (a paused download must stay reachable — no auto-hide).
 * Once nothing is active or paused any more — everything finished, or only
 * stopped downloads remain in the list — it stays for `hideAfterMs` (one
 * minute) and then hides; the next enqueue brings it back. Clicking it opens
 * the "Downloads" list.
 */
import { useEffect, useState, type ReactElement } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { DownloadIcon, PauseIcon } from "@aeriosiq/ui/icons";
import { useDownloadManager } from "./DownloadManagerProvider.js";
import { WORDS } from "./words.js";

/** How long the pill lingers after the last download finished or stopped. */
export const PILL_LINGER_MS = 60_000;

export interface DownloadStatusBarProps {
  onOpen: () => void;
  /** linger after idle; tests pass 0 */
  hideAfterMs?: number | undefined;
  /**
   * DI-3-S48-FE — the shell places the pill in its bottom dock (a fixed row
   * at the bottom centre, beside the intake pill): no own fixed position.
   */
  docked?: boolean | undefined;
}

export function DownloadStatusBar({
  onOpen,
  hideAfterMs = PILL_LINGER_MS,
  docked = false,
}: DownloadStatusBarProps): ReactElement | null {
  const { totals } = useDownloadManager();
  const paused = totals.paused > 0;
  // "active" for the pill = something is or will be downloading, or waits paused
  const active = totals.active > 0 || paused;
  const listed = totals.total > 0;
  // visible while active; after the last item goes idle, for `hideAfterMs`
  const [lingering, setLingering] = useState(false);
  useEffect(() => {
    if (active) {
      setLingering(true);
      return;
    }
    if (!listed) {
      setLingering(false);
      return;
    }
    const t = window.setTimeout(() => {
      setLingering(false);
    }, hideAfterMs);
    return () => {
      window.clearTimeout(t);
    };
  }, [active, listed, hideAfterMs]);
  if (!active && !lingering) return null;

  const pct =
    totals.filesTotal > 0
      ? Math.min(100, Math.round((totals.filesDone / totals.filesTotal) * 100))
      : 0;
  const line =
    paused && totals.active === 0
      ? WORDS.pillPaused(totals.filesDone, totals.filesTotal)
      : active
        ? WORDS.pill(
            totals.filesDone,
            totals.filesTotal,
            totals.done + (totals.running ? 1 : 0),
            totals.total,
          )
        : totals.stopped > 0
          ? WORDS.pillStopped(totals.stopped)
          : WORDS.pillDone(totals.filesDone);
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={WORDS.barOpen}
      aria-live="polite"
      data-testid="dl-statusbar"
      data-active={active ? "true" : "false"}
      data-paused={paused ? "true" : "false"}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        ...(docked
          ? {}
          : {
              position: "fixed",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: 16,
              zIndex: (t: { zIndex: { appBar: number } }) =>
                t.zIndex.appBar - 1,
            }),
        pointerEvents: "auto",
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        pl: 1,
        pr: 1.5,
        height: 32,
        // D72 — pills are 999px
        borderRadius: 999,
        bgcolor: "background.default",
        border: 1,
        borderColor: "divider",
        boxShadow: (t) => `0 1px 4px ${t.palette.action.hover}`,
        cursor: "pointer",
        "&:hover": { bgcolor: "action.hover" },
        maxWidth: "calc(100vw - 32px)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 20,
          height: 20,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {paused && totals.active === 0 ? (
          <PauseIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        ) : active ? (
          <CircularProgress
            variant={totals.running?.progress ? "determinate" : "indeterminate"}
            value={pct}
            size={20}
            thickness={5}
            aria-label={WORDS.progressLabel}
            data-testid="dl-statusbar-progress"
          />
        ) : (
          <DownloadIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        )}
      </Box>
      <Typography
        variant="caption"
        noWrap
        sx={{ fontVariantNumeric: "tabular-nums" }}
        data-testid="dl-statusbar-line"
      >
        {line}
      </Typography>
    </Box>
  );
}
